<template>
  <div class="auth-wrapper auth-v1">
    <div class="auth-inner">
      <v-card class="auth-card" elevation="5">
        <!-- Logo -->
        <v-card-title class="d-flex align-center justify-center py-7" style="background: transparent">
          <router-link to="/" class="d-flex align-center">
            <v-img
              :src="require('@/assets/images/logos/LogoACS.png')"
              max-height="50px"
              max-width="80px"
              alt="logo"
              contain
              class="me-3"
            ></v-img>
          </router-link>
        </v-card-title>

        <!-- Titre -->
        <v-card-text>
          <p class="text-1xl font-weight-semibold text--primary mb-1" style="text-align: center">
            {{ $vuetify.lang.t('$vuetify.changePassword.title') }}
          </p>
          <p class="text-sm text--secondary" style="text-align: center">
            {{ $vuetify.lang.t('$vuetify.changePassword.subtitle') }}
          </p>
        </v-card-text>

        <!-- Formulaire -->
        <v-card-text>
          <v-form ref="form" v-model="valid">

            <!-- Mot de passe actuel -->
            <v-text-field
              v-model="currentPassword"
              outlined
              :label="$vuetify.lang.t('$vuetify.changePassword.currentPassword')"
              :type="isCurrentPasswordVisible ? 'text' : 'password'"
              :append-icon="isCurrentPasswordVisible ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
              @click:append="isCurrentPasswordVisible = !isCurrentPasswordVisible"
              :rules="[v => !!v || $vuetify.lang.t('$vuetify.changePassword.currentPasswordRequired')]"
              class="mb-3"
            ></v-text-field>

            <!-- Nouveau mot de passe -->
            <v-text-field
              v-model="newPassword"
              outlined
              :label="$vuetify.lang.t('$vuetify.changePassword.newPassword')"
              :type="isNewPasswordVisible ? 'text' : 'password'"
              :append-icon="isNewPasswordVisible ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
              @click:append="isNewPasswordVisible = !isNewPasswordVisible"
              :rules="passwordRules"
              class="mb-3"
            ></v-text-field>

            <!-- Confirmation -->
            <v-text-field
              v-model="confirmPassword"
              outlined
              :label="$vuetify.lang.t('$vuetify.changePassword.confirmPassword')"
              :type="isConfirmPasswordVisible ? 'text' : 'password'"
              :append-icon="isConfirmPasswordVisible ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
              @click:append="isConfirmPasswordVisible = !isConfirmPasswordVisible"
              :rules="confirmPasswordRules"
              class="mb-3"
            ></v-text-field>

            <!-- Erreurs -->
            <v-alert v-if="errorMessage" type="error" class="mb-3">
              {{ errorMessage }}
            </v-alert>

            <v-btn
              block
              color="#3BAFDD"
              class="mt-2 white--text"
              :disabled="!valid || loading"
              :loading="loading"
              @click="submit"
            >
              {{ $vuetify.lang.t('$vuetify.changePassword.submit') }}
            </v-btn>

          </v-form>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import { mdiEyeOutline, mdiEyeOffOutline } from '@mdi/js'
import { config } from '@/config'

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&,;:(|`^.<>{}/])[A-Za-z\d@$!%*?&,;:(|`^.<>{}/]{15,}$/

export default {
  name: 'ChangePassword',

  data() {
    return {
      valid: false,
      loading: false,
      errorMessage: null,

      currentPassword: '',
      newPassword: '',
      confirmPassword: '',

      isCurrentPasswordVisible: false,
      isNewPasswordVisible: false,
      isConfirmPasswordVisible: false,

      icons: { mdiEyeOutline, mdiEyeOffOutline },

      appConfig: config,
    }
  },

  computed: {
    passwordRules() {
      return [
        v => !!v || this.$vuetify.lang.t('$vuetify.changePassword.newPasswordRequired'),
        v => PASSWORD_REGEX.test(v) || this.$vuetify.lang.t('$vuetify.changePassword.validationMessage'),
      ]
    },
    confirmPasswordRules() {
      return [
        v => !!v || this.$vuetify.lang.t('$vuetify.changePassword.confirmPasswordRequired'),
        v => v === this.newPassword || this.$vuetify.lang.t('$vuetify.changePassword.passwordMismatch'),
      ]
    },
  },

  methods: {
    async submit() {
      if (!this.$refs.form.validate()) return

      this.loading = true
      this.errorMessage = null

      try {
        const token = localStorage.getItem('token')
        const response = await fetch(this.appConfig.$api_url + '/auth/change-password', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token,
          },
          body: JSON.stringify({
            currentPassword: this.currentPassword,
            newPassword: this.newPassword,
          }),
        })

        if (response.status === 200) {
          // Mettre à jour le flag en localStorage
          const user = JSON.parse(localStorage.getItem('user') || '{}')
          user.forcePasswordChange = false
          localStorage.setItem('user', JSON.stringify(user))

          // Rediriger vers le dashboard
          this.$router.push('/dashboard/' + (user.lang || 'fr'))
        } else if (response.status === 400) {
          const errorObject = await response.json()
          const firstCode = errorObject?.fields?.[0]?.codes?.[0]?.code
          if (firstCode === 'USER_NOT_ALLOWED') {
            this.errorMessage = this.$vuetify.lang.t('$vuetify.changePassword.incorrectCurrentPassword')
          } else {
            this.errorMessage = this.$vuetify.lang.t('$vuetify.changePassword.internalError')
          }
        } else {
          this.errorMessage = this.$vuetify.lang.t('$vuetify.changePassword.internalError')
        }
      } catch (e) {
        this.errorMessage = this.$vuetify.lang.t('$vuetify.changePassword.internalError')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
